/**
 * Actycrea: send.esm.js (ES6) — Cloudflare Turnstile
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { forceDownload } from '../helpers/helpers.js';
import { Toasts } from '../helpers/toasts.js';
import { setActiveLoading , removeActiveLoading } from '../helpers/spinner.js';
import { gsap } from 'gsap';

const SELECTOR            = `data-${PREFIX}-mysd`;
const SELECTOR_DATA       = `[${SELECTOR}]`;

const SELECTOR_EQUAL      = `data-${PREFIX}-equal`;
const SELECTOR_DATA_EQUAL = `[${SELECTOR_EQUAL}]`;
const SELECTOR_SUBMIT     = '[type="submit"]';
const SELECTOR_EXPAND     = '[data-expand]';
const SELECTOR_SEND       = '[data-val-send]';
const SELECTOR_FILE       = '[type="file"]'; // <-- Añadido indispensable

const CLASS_HIDDEN        = 'd-none';

const DEFAULTS = {
  ajax     : true ,
  interval : 5000 ,
  id       : '' ,
  cid      : 'contact' ,
};

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// cache global del loader de Turnstile
let _turnstileLoader = null;

/**
 * Inyecta un script externo en el documento.
 *
 * @param {string} src
 * @returns {Promise<void>}
 */
function _injectScript( src ){
  return new Promise( ( resolve , reject ) => {
    const current = document.querySelector( `script[src="${src}"]` );
    if ( current ) {
      if ( typeof window.turnstile === 'object' ) {
        resolve( );
        return;
      }

      current.addEventListener( 'load' , ( ) => resolve( ) , { once : true } );
      current.addEventListener( 'error' , ( ) => reject( new Error( 'No se pudo cargar Cloudflare Turnstile' ) ) , { once : true } );
      return;
    }

    const s = document.createElement( 'script' );
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = ( ) => resolve( );
    s.onerror = ( ) => reject( new Error( 'No se pudo cargar Cloudflare Turnstile' ) );
    document.head.appendChild( s );
  } );
}

/**
 * Espera a que la API global de Turnstile esté disponible.
 *
 * @param {number} [timeout=10000]
 * @returns {Promise<any>}
 */
function _waitForTurnstile( timeout = 10000 ){
  return new Promise( ( resolve , reject ) => {
    const start = Date.now( );

    const tick = ( ) => {
      const api = window.turnstile;
      if ( api && typeof api.render === 'function' && typeof api.execute === 'function' ) {
        resolve( api );
        return;
      }

      if ( Date.now( ) - start >= timeout ) {
        reject( new Error( 'turnstile no disponible' ) );
        return;
      }

      window.requestAnimationFrame( tick );
    };

    tick( );
  } );
}

/**
 * Carga la API de Cloudflare Turnstile una única vez.
 *
 * @returns {Promise<any>}
 */
async function loadTurnstile( ){
  if ( _turnstileLoader ) return _turnstileLoader;

  _turnstileLoader = ( async ( ) => {
    if ( typeof window.turnstile !== 'object' ) {
      await _injectScript( TURNSTILE_SRC );
    }

    return _waitForTurnstile( );
  } )( );

  return _turnstileLoader;
}

export class MySD {
  /**
   * @param {HTMLFormElement} element
   * @param {Object} config
   */
  constructor( element , config = { } ){
    if ( !( element instanceof HTMLFormElement ) ) throw new Error( 'MySD: element inválido' );

    this._element  = element;
    this._config   = { ...DEFAULTS , ...config };

    console.log( '%c=> send.esm.js => Activated' , 'background-color:green;color:#fff;padding: 5px 10px;' );
    console.table( this._config );

    this._turnstileWidgetId = null;
    this._turnstileContainer = null;
    this._turnstileAction = '';
    this._turnstilePromise = null;
    this._turnstileResolve = null;
    this._turnstileReject = null;

    this._expandNode = this._element.querySelector( SELECTOR_EXPAND );
    this._expandInput = null;

    if ( this._expandNode ) {
      const expandTargetId = this._expandNode.dataset.expand || '';
      this._expandInput = expandTargetId ? document.getElementById( expandTargetId ) : null;
    }

    this._submit   = element.querySelector( SELECTOR_SUBMIT );
    
    this.attach     = this._element.querySelector( SELECTOR_FILE );
    this.fileText   = this._element.querySelector( '[data-wp-filecustom]' );
    this._mimes     = [ 'application/pdf' , 'image/jpeg' , 'image/png' ];
    this._maxSize   = 8 * 1024 * 1024;

    this._method   = String( element.getAttribute( 'method' ) || 'POST' ).toUpperCase( );
    this._action   = element.getAttribute( 'action' ) || ( window.wphack?.ajax_url || '' );

    this._onSubmit      = this._onSubmit.bind( this );
    this._onEqualChange = this._onEqualChange.bind( this );
    this._onExpandFocus = this._onExpandFocus.bind( this );
    this._onExpandBlur  = this._onExpandBlur.bind( this );
    this._onSendTarget  = this._onSendTarget.bind( this );
    this._docValidation = this._docValidation.bind( this ); // <-- Añadido indispensable

    this._bind( );
  }

  /* =================
   * Helpers
   * ================*/
  /**
   * Obtiene el elemento destino a partir del trigger.
   *
   * Soporta:
   * - data-val-send="#target"
   * - data-bs-target="#target"
   * - data-target="#target"
   * - href="#target"
   *
   * @param {HTMLElement} trigger
   * @returns {HTMLElement|null}
   */
  _getTargetFromTrigger( trigger ){
    if ( !( trigger instanceof HTMLElement ) ) return null;

    const rawSelector =
      trigger.getAttribute( 'data-val-send' ) ||
      trigger.getAttribute( 'data-bs-target' ) ||
      trigger.getAttribute( 'data-target' ) ||
      trigger.getAttribute( 'href' ) ||
      '';

    if ( !rawSelector || rawSelector === '#' ) return null;

    try {
      return document.querySelector( rawSelector );
    } catch ( error ) {
      console.error( 'MySD => _getTargetFromTrigger => selector inválido => ' , rawSelector , error );
      return null;
    }
  }

  /* =================
   * Listeners
   * ================*/
  _bind( ){
    this._element.addEventListener( 'submit' , this._onSubmit , false );
    this._element.addEventListener( 'change' , ( ev ) => {
      const t = ev.target;
      if ( t.matches( SELECTOR_DATA_EQUAL ) ) this._onEqualChange( ev );
      if ( t.matches( SELECTOR_SEND ) ) this._onSendTarget( ev );
      if ( this.attach && t === this.attach ) this._docValidation( ev ); // <-- Añadido indispensable
    } , false );
    this._element.addEventListener( 'keyup' , ( ev ) => {
      const t = ev.target;
      if ( t.matches( SELECTOR_DATA_EQUAL ) ) this._onEqualChange( ev );
    } , false );

    if ( this._expandInput ) {
      this._expandInput.addEventListener( 'focus' , this._onExpandFocus , false );
      this._expandInput.addEventListener( 'blur' , this._onExpandBlur , false );
    }
  }

  /* =================
   * Validaciones
   * ================*/
  _onEqualChange( ev ){
    const src = ev.target;
    const sel = src.getAttribute( SELECTOR_EQUAL );
    const target = sel ? this._element.querySelector( sel ) : null;
    if ( !target ) { src.setCustomValidity( '' ); return; }
    if ( String( src.value ) !== String( target.value ) ) {
      src.setCustomValidity( 'Debes repetir el mismo valor.' );
    } else {
      src.setCustomValidity( '' );
    }
  }

  /**
   * Replica el comportamiento antiguo de `data-val-send`.
   *
   * Funcionamiento:
   * - El campo origen suele ser un `<select data-val-send="#target">`
   * - Busca la opción seleccionada
   * - Si no hay valor seleccionado, limpia el target
   * - Si la opción tiene `data-value`, copia ese valor al target
   * - Si no tiene `data-value`, usa el propio `value` de la opción
   *
   * @param {Event} ev
   * @returns {void}
   */
  _onSendTarget( ev ){
    const source = ev.target;
    if ( !( source instanceof HTMLSelectElement ) && !( source instanceof HTMLElement ) ) return;

    const target = this._getTargetFromTrigger( source );
    if ( !target ) return;

    const value = String( source.value || '' );
    const selected = source.querySelector( `option[value="${CSS.escape( value )}"]` );

    if ( value === '' || !selected ) {
      target.value = '';
    } else {
      target.value = selected.dataset.value ?? value;
    }

    target.dispatchEvent( new Event( 'input' , { bubbles : true } ) );
    target.dispatchEvent( new Event( 'change' , { bubbles : true } ) );
  }

  /**
   * Valida el archivo adjunto (MIME y tamaño máximo).
   *
   * @param {Event} ev
   */
  _docValidation( ev ){
    const target = ev.target;

    // Al cambiar de archivo, limpiamos el texto previo inmediatamente
    if ( this.fileText ) {
      this.fileText.textContent = '';
    }

    if ( target.files.length > 0 ) {
      // Recuperamos los mensajes traducidos desde el HTML (o un fallback por si acaso)
      const msgMime = target.dataset.msgMime || 'MIME no permitido';
      const msgSize = target.dataset.msgSize || 'Excede el tamaño máximo permitido';

      for ( const file of target.files ) {
        if ( !this._mimes.includes( file.type ) ) {
          // Si falla, registramos el error nativo
          target.setCustomValidity( `${msgMime}: ${file.name}` );
          
          // Y pintamos el mensaje de error traducido directamente en tu span custom
          if ( this.fileText ) {
            this.fileText.textContent = msgMime;
          }
          return false;
        }
        if ( file.size > this._maxSize ) {
          // Si pesa más de 8MB, registramos el error nativo
          target.setCustomValidity( `${msgSize}: ${file.name}` );
          
          // Y pintamos el mensaje de error traducido directamente en tu span custom
          if ( this.fileText ) {
            this.fileText.textContent = msgSize;
          }
          return false;
        }
        
        // Si el archivo es totalmente válido, lo pintamos en el span customizado
        if ( this.fileText ) {
          this.fileText.textContent = file.name;
        }
      }
    }
    target.setCustomValidity( '' );
    return true;
  }

  _validateForm( ){
    const form = this._element;
    
    // Evaluamos el archivo manualmente
    let isFileValid = true;
    if ( this.attach ) {
      isFileValid = this._docValidation( { target: this.attach } );
    }

    if ( form.classList.contains( 'needs-validation' ) && !form.classList.contains( 'was-validated' ) ) {
      form.classList.add( 'was-validated' );
    }

    // El formulario solo es válido si pasa la validación nativa y el archivo también es correcto
    return form.checkValidity( ) && isFileValid;
  }

  _onExpandFocus( ){
    if ( !this._expandNode ) return;

    gsap.killTweensOf( this._expandNode );
    gsap.set( this._expandNode , { overflow : 'hidden' } );
    gsap.to( this._expandNode , {
      height    : 'auto',
      autoAlpha : 1,
      duration  : 0.5,
      ease      : 'power1.inOut',
    } );
  }

  _onExpandBlur( ev ){
    if ( !this._expandNode ) return;
    if ( !ev?.target ) return;

    if ( String( ev.target.value || '' ).trim( ) !== '' ) return;

    gsap.killTweensOf( this._expandNode );
    gsap.to( this._expandNode , {
      height    : 0,
      autoAlpha : 0,
      duration  : 0.5,
      ease      : 'power1.inOut',
      onComplete : ( ) => {
        this._expandNode.style.overflow = 'hidden';
      },
    } );
  }

  /* =================
   * Turnstile
   * ================*/
  /**
   * Crea o recrea el widget de Turnstile.
   *
   * @param {string} sitekey
   * @param {string} action
   * @returns {Promise<number|string>}
   */
  async _ensureTurnstileWidget( sitekey , action ){
    const api = await loadTurnstile( );

    if ( !this._turnstileContainer ) {
      this._turnstileContainer = document.createElement( 'div' );
      this._turnstileContainer.classList.add( CLASS_HIDDEN );
      this._turnstileContainer.setAttribute( 'aria-hidden' , 'true' );
      document.body.appendChild( this._turnstileContainer );
    }

    if ( this._turnstileWidgetId !== null && this._turnstileAction !== action ) {
      if ( typeof api.remove === 'function' ) {
        api.remove( this._turnstileWidgetId );
      }
      this._turnstileWidgetId = null;
      this._turnstileContainer.innerHTML = '';
    }

    if ( this._turnstileWidgetId !== null ) return this._turnstileWidgetId;

    this._turnstileAction = action;
    this._turnstileWidgetId = api.render( this._turnstileContainer , {
      sitekey : sitekey ,
      size    : 'compact' ,
      action  : action ,
      callback : ( token ) => {
        if ( typeof this._turnstileResolve === 'function' ) {
          this._turnstileResolve( token );
        }
        this._turnstilePromise = null;
        this._turnstileResolve = null;
        this._turnstileReject = null;
      } ,
      'error-callback' : ( code ) => {
        if ( typeof this._turnstileReject === 'function' ) {
          this._turnstileReject( new Error( `Turnstile error: ${code || 'unknown'}` ) );
        }
        this._turnstilePromise = null;
        this._turnstileResolve = null;
        this._turnstileReject = null;
      } ,
      'expired-callback' : ( ) => {
        if ( typeof this._turnstileReject === 'function' ) {
          this._turnstileReject( new Error( 'Turnstile token expirado' ) );
        }
        this._turnstilePromise = null;
        this._turnstileResolve = null;
        this._turnstileReject = null;
      } ,
      'timeout-callback' : ( ) => {
        if ( typeof this._turnstileReject === 'function' ) {
          this._turnstileReject( new Error( 'Turnstile timeout' ) );
        }
        this._turnstilePromise = null;
        this._turnstileResolve = null;
        this._turnstileReject = null;
      } ,
    } );

    return this._turnstileWidgetId;
  }

  /**
   * Ejecuta Turnstile y devuelve el token generado.
   *
   * @param {string} sitekey
   * @param {string} action
   * @returns {Promise<string>}
   */
  async _getTurnstileToken( sitekey , action ){
    const api = await loadTurnstile( );
    const widgetId = await this._ensureTurnstileWidget( sitekey , action );

    if ( this._turnstilePromise ) {
      return this._turnstilePromise;
    }

    this._turnstilePromise = new Promise( ( resolve , reject ) => {
      this._turnstileResolve = resolve;
      this._turnstileReject = reject;
    } );

    try {
      api.reset( widgetId );
      api.execute( widgetId );
      return await this._turnstilePromise;
    } catch ( error ) {
      this._turnstilePromise = null;
      this._turnstileResolve = null;
      this._turnstileReject = null;
      throw error;
    }
  }

  resetTurnstile( ){
    if ( typeof window.turnstile !== 'object' ) return;
    if ( this._turnstileWidgetId === null ) return;
    window.turnstile.reset( this._turnstileWidgetId );
  }

  /* =================
   * Envío
   * ================*/
  async _onSubmit( ev ){
    ev.preventDefault( );
    ev.stopPropagation( );

    if ( !this._validateForm( ) ) {
      if ( this._expandNode ) {
        this._onExpandFocus( );
      }
      return;
    }

    setActiveLoading( this._element );

    const sitekey = this._submit?.dataset?.sitekey || '';
    const action  = this._submit?.dataset?.action || 'submit';

    try {
      let token = '';
      if ( sitekey ) {
        token = await this._getTurnstileToken( sitekey , action );
      }
      await this._send( token );
    } catch ( err ) {
      this._handleError( err );
    }
  }

  async _send( token ){
    const data = new FormData( this._element );
    if ( token ) data.append( 'cf-turnstile-response' , token );
    this.debug( data );

    const res = await fetch( this._action , {
      method : this._method ,
      body   : data
    } );

    const payload = await res.json( );
    if ( !res.ok ) throw payload;
    this._handleSuccess( payload );
  }

  /* =================
   * Respuestas
   * ================*/
  _handleSuccess( data ){
    console.info( '%cMySD => _handleSuccess => data => ' , 'color:green;' , data );
    if ( data?.data?.url && data?.data?.filename ) {
      forceDownload( data?.data?.url , data?.data?.filename );
    }
    Toasts.show( data?.data?.html );
    this._element.classList.add( 'sended' );
    this._element.classList.remove( 'was-validated' );
    this._element.reset( );

    if ( this.fileText ) {
      this.fileText.textContent = ''; 
    }

    if ( this._expandNode ) {
      gsap.killTweensOf( this._expandNode );
      gsap.to( this._expandNode , {
        height    : 0,
        autoAlpha : 0,
        duration  : 0.5,
        ease      : 'power1.inOut',
        onComplete : ( ) => {
          this._expandNode.style.overflow = 'hidden';
        },
      } );
    }

    this.resetTurnstile( );
    removeActiveLoading( this._element );
    this.setDataLayer( 'success' );
  }

  _handleError( error ){
    console.error( '%cMySD => _handleError => error => ' , 'color:red;' , error );
    if ( error && error.data ) {
      console.log( '%c=> DETALLE DEL ERROR DE WORDPRESS:', 'background: #000; color: #ff0000; padding: 4px;', error.data );
    }
    Toasts.show( error?.data?.html );
    this.resetTurnstile( );
    removeActiveLoading( this._element );
    this.setDataLayer( 'error' );
  }

  /* ============
   * Utilidades
   * ===========*/
  debug( formData ){
    console.log( 'MySD => debug => formData => ' , formData );
    const out = { };
    for ( const [ k , v ] of formData.entries( ) ) {
      out[ k ] = v instanceof File
        ? {
            name : v.name,
            size : v.size,
            type : v.type,
          }
        : v;
    }
    console.log( 'MySD => debug => out => ' , out );
  }

  setDataLayer( status ) {
    const layer = {
      event      : 'formSubmit' ,
      formId     : this._config.cid ,
      formName   : this._config.cid.replace( /^./ , this._config.cid[ 0 ].toUpperCase( ) ) ,
      status     : status
    };
    window.dataLayer = window.dataLayer || [ ];
    window.dataLayer.push( layer );
    console.log( 'MySD => setDataLayer => dataLayer => ' , layer );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if ( el._mysdInstance ) return;
      let cfg = { };
      const raw = el.getAttribute( SELECTOR );
      if ( raw ) {
        try {
          cfg = JSON.parse( raw );
        } catch {
          cfg = { };
        }
      }
      el._mysdInstance = new MySD( el , cfg );
      instances.push( el._mysdInstance );
    } );
    return instances;
  }
}

export default MySD;